import React, { useState, useEffect } from 'react';
import { Card, Heading, Text, Flex, Avatar, Separator, Box } from '@radix-ui/themes';
import { StarIcon, TargetIcon, CheckIcon, CalendarIcon, CircleIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Member, FILE_SERVER_URL, apiService, FeesRecord, FeesResponse, AttendanceRecord, AttendanceResponse } from './services/api';
import AppBar from './components/AppBar';
import NavigationDrawer from './components/NavigationDrawer';

interface FeesDetailsProps {
    students: Member[];
    selectedStudent: Member | null;
    onStudentChange: (student: Member) => void;
}

interface PendingFeesStatus {
    hasAttendance: boolean;
    attendanceDays: number;
    feesPending: boolean;
    pendingPeriod: string | null;
    currentMonthAttendance: number;
}

const FeesDetails: React.FC<FeesDetailsProps> = ({ students, selectedStudent, onStudentChange }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [feesData, setFeesData] = useState<FeesResponse | null>(null);
    const [attendanceData, setAttendanceData] = useState<AttendanceResponse | null>(null);
    const [pendingFeesStatus, setPendingFeesStatus] = useState<PendingFeesStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Check pending fees based on attendance
    const checkPendingFees = (attendance: AttendanceRecord[], fees: FeesRecord[]): PendingFeesStatus => {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();

        // Count attendance days in current month
        const currentMonthAttendance = attendance.filter(record => {
            const recordDate = new Date(record.ATTENDENCE);
            return recordDate.getMonth() + 1 === currentMonth &&
                recordDate.getFullYear() === currentYear;
        });

        const attendanceDays = currentMonthAttendance.length;

        // Check for pending fees from any month where student had sufficient attendance
        let feesPending = false;
        let pendingPeriod = null;

        // Group attendance by month
        const attendanceByMonth = attendance.reduce((acc, record) => {
            const recordDate = new Date(record.ATTENDENCE);
            const monthKey = `${recordDate.getFullYear()}-${recordDate.getMonth() + 1}`;

            if (!acc[monthKey]) {
                acc[monthKey] = [];
            }
            acc[monthKey].push(record);
            return acc;
        }, {} as Record<string, AttendanceRecord[]>);

        // Check each month for pending fees
        for (const [monthKey, monthAttendance] of Object.entries(attendanceByMonth)) {
            if (monthAttendance.length >= 3) {
                // Student had sufficient attendance this month - check for fees
                const [year, month] = monthKey.split('-').map(Number);

                const monthFees = fees.filter(fee => {
                    const feeDate = new Date(fee.fromDate.split('-').reverse().join('-'));
                    return feeDate.getMonth() + 1 === month &&
                        feeDate.getFullYear() === year;
                });

                if (monthFees.length === 0) {
                    // Fees pending for this month
                    feesPending = true;
                    const pendingDate = new Date(year, month - 1);
                    pendingPeriod = pendingDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    break; // Show the first pending month
                }
            }
        }

        // Check if student has sufficient attendance in current month
        const hasSufficientAttendance = attendanceDays >= 3;

        return {
            hasAttendance: hasSufficientAttendance,
            attendanceDays,
            feesPending,
            pendingPeriod,
            currentMonthAttendance: attendanceDays
        };
    };

    useEffect(() => {
        if (!selectedStudent) return;

        setLoading(true);
        setError(null);

        // Fetch both attendance and fees data
        Promise.all([
            apiService.getAttendance(selectedStudent.memberId),
            apiService.getFeesDetails(selectedStudent.memberId)
        ]).then(([attendanceResponse, feesResponse]) => {
            if (attendanceResponse.success && feesResponse.success) {
                setAttendanceData(attendanceResponse);
                setFeesData(feesResponse);

                // Check for pending fees
                const pendingStatus = checkPendingFees(
                    attendanceResponse.attendance,
                    feesResponse.fees
                );
                setPendingFeesStatus(pendingStatus);
            } else {
                setError('Failed to fetch data');
            }
        }).catch(() => setError('Failed to fetch data'))
            .finally(() => setLoading(false));
    }, [selectedStudent]);

    // Calculate fees statistics
    const calculateStats = () => {
        if (!feesData?.fees) return { totalPayments: 0, lastPaymentDate: null, nextDueDate: null };

        const fees = feesData.fees;
        const totalPayments = fees.length;

        // Sort by entry_date to get the most recent payment
        const sortedFees = [...fees].sort((a, b) =>
            new Date(b.entry_date.split('-').reverse().join('-')).getTime() -
            new Date(a.entry_date.split('-').reverse().join('-')).getTime()
        );

        const lastPayment = sortedFees[0];
        const lastPaymentDate = lastPayment ? lastPayment.entry_date : null;

        // Calculate next due date (assuming monthly payments)
        let nextDueDate = null;
        if (lastPayment) {
            const lastToDate = new Date(lastPayment.toDate.split('-').reverse().join('-'));
            nextDueDate = new Date(lastToDate);
            nextDueDate.setMonth(nextDueDate.getMonth() + 1);
        }

        return {
            totalPayments,
            lastPaymentDate,
            nextDueDate
        };
    };

    // Format date to proper format
    const formatDate = (dateString: string) => {
        const date = new Date(dateString.split('-').reverse().join('-'));
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Format date range
    const formatDateRange = (fromDate: string, toDate: string) => {
        const from = new Date(fromDate.split('-').reverse().join('-'));
        const to = new Date(toDate.split('-').reverse().join('-'));

        const fromFormatted = from.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
        const toFormatted = to.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });

        return `${fromFormatted} - ${toFormatted}`;
    };

    const stats = calculateStats();

    return (
        <div className="home-page">
            <AppBar
                onMenuClick={() => setIsDrawerOpen(true)}
                students={students}
                selectedStudent={selectedStudent}
                onStudentChange={onStudentChange}
            />
            <NavigationDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                onLogout={() => window.location.href = '/'}
                selectedStudent={selectedStudent}
            />
            <main className="home-content">
                <div className="content-container">
                    {/* Welcome Section */}
                    <Card className="welcome-card">
                        <Flex align="center" gap="4">
                            <Avatar
                                size="4"
                                src={selectedStudent?.avatar && selectedStudent?.avatar !== 'NoImageFound.png' ? `${FILE_SERVER_URL}/show_files/${selectedStudent.avatar}` : undefined}
                                fallback={selectedStudent?.fullName?.charAt(0) || 'S'}
                            />
                            <Flex direction="column" gap="1">
                                <Heading size="6">Welcome, {selectedStudent?.fullName || 'Student'}!</Heading>
                                <Text size="2" color="gray">Here are your fees and payment details.</Text>
                            </Flex>
                        </Flex>
                    </Card>

                    {/* Stats Section */}
                    <div className="profile-stats-grid">
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="blue">
                                    {stats.totalPayments}
                                </Text>
                                <Text size="2" color="gray">Total Payments</Text>
                            </Flex>
                        </Card>
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="green">
                                    {pendingFeesStatus?.currentMonthAttendance || 0}
                                </Text>
                                <Text size="2" color="gray">This Month Attendance</Text>
                            </Flex>
                        </Card>
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color={pendingFeesStatus?.feesPending ? "red" : "green"}>
                                    {pendingFeesStatus?.feesPending ? "Pending" : "Up to Date"}
                                </Text>
                                <Text size="2" color="gray">Fee Status</Text>
                            </Flex>
                        </Card>
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="orange">
                                    {stats.lastPaymentDate ? formatDate(stats.lastPaymentDate) : 'N/A'}
                                </Text>
                                <Text size="2" color="gray">Last Payment</Text>
                            </Flex>
                        </Card>
                    </div>

                    {/* Payment History */}
                    <Card className="activity-card">
                        <Heading size="4" mb="4">Fees Paid History</Heading>
                        {loading ? (
                            <Text size="3">Loading fees details...</Text>
                        ) : error ? (
                            <Text size="3" color="red">{error}</Text>
                        ) : feesData?.fees ? (
                            <Flex direction="column" gap="3">
                                {/* Pending Fees Warning */}
                                {pendingFeesStatus?.feesPending && (
                                    <Flex align="center" gap="3" className="activity-item" style={{ border: '2px solid var(--red-9)', backgroundColor: 'var(--red-1)', borderRadius: '8px', padding: '12px' }}>
                                        <div className="activity-icon" style={{ backgroundColor: 'var(--red-9)' }}>
                                            <ExclamationTriangleIcon width="16" height="16" style={{ color: 'white' }} />
                                        </div>
                                        <Flex direction="column" gap="1">
                                            <Text size="2" weight="bold" color="red">Fees Pending</Text>
                                            <Text size="1" color="gray">
                                                Attendance: {pendingFeesStatus.attendanceDays} days ✓
                                            </Text>
                                            <Text size="1" color="gray">
                                                Status: Fees pending for {pendingFeesStatus.pendingPeriod}
                                            </Text>
                                        </Flex>
                                        <div className="activity-icon" style={{ backgroundColor: 'var(--red-9)', marginLeft: 'auto' }}>
                                            <ExclamationTriangleIcon width="16" height="16" style={{ color: 'white' }} />
                                        </div>
                                    </Flex>
                                )}

                                {/* Paid Fees History */}
                                {feesData.fees
                                    .sort((a, b) => {
                                        // Sort by entry_date in descending order (most recent first)
                                        const dateA = new Date(a.entry_date.split('-').reverse().join('-'));
                                        const dateB = new Date(b.entry_date.split('-').reverse().join('-'));
                                        return dateB.getTime() - dateA.getTime();
                                    })
                                    .map((fee) => (
                                        <Flex key={fee.id} align="center" gap="3" className="activity-item">
                                            <div className="activity-icon">
                                                <CheckIcon width="16" height="16" />
                                            </div>
                                            <Flex direction="column" gap="1">
                                                <Text size="2" weight="bold">Fees Paid</Text>
                                                <Text size="1" color="gray">
                                                    {formatDateRange(fee.fromDate, fee.toDate)}
                                                </Text>
                                                <Text size="1" color="gray" style={{ fontStyle: 'italic' }}>
                                                    Paid on: {formatDate(fee.entry_date)}
                                                </Text>
                                            </Flex>
                                            <div className="activity-icon" style={{ backgroundColor: 'var(--green-9)', marginLeft: 'auto' }}>
                                                <CheckIcon width="16" height="16" style={{ color: 'white' }} />
                                            </div>
                                        </Flex>
                                    ))}
                            </Flex>
                        ) : (
                            <Text size="3">No fees data available</Text>
                        )}
                    </Card>

                    {/* Fee Structure - Temporarily Disabled */}
                    {/* <Card className="activity-card">
                        <Heading size="4" mb="4">Fee Structure</Heading>
                        <Flex direction="column" gap="3">
                            <Flex align="center" gap="3" className="activity-item">
                                <div className="activity-icon">
                                    <CalendarIcon width="16" height="16" />
                                </div>
                                <Flex direction="column" gap="1">
                                    <Text size="2" weight="bold">Monthly Fee</Text>
                                    <Text size="1" color="gray">Standard monthly fee</Text>
                                </Flex>
                                <div className="activity-icon" style={{ backgroundColor: 'var(--blue-9)', marginLeft: 'auto' }}>
                                    <StarIcon width="16" height="16" />
                                </div>
                            </Flex>
                            <Flex align="center" gap="3" className="activity-item">
                                <div className="activity-icon">
                                    <TargetIcon width="16" height="16" />
                                </div>
                                <Flex direction="column" gap="1">
                                    <Text size="2" weight="bold">Payment Schedule</Text>
                                    <Text size="1" color="gray">Monthly payments</Text>
                                </Flex>
                                <div className="activity-icon" style={{ backgroundColor: 'var(--green-9)', marginLeft: 'auto' }}>
                                    <CheckIcon width="16" height="16" />
                                </div>
                            </Flex>
                            <Flex align="center" gap="3" className="activity-item">
                                <div className="activity-icon">
                                    <StarIcon width="16" height="16" />
                                </div>
                                <Flex direction="column" gap="1">
                                    <Text size="2" weight="bold">Payment Method</Text>
                                    <Text size="1" color="gray">Online/Cash</Text>
                                </Flex>
                                <div className="activity-icon" style={{ backgroundColor: 'var(--orange-9)', marginLeft: 'auto' }}>
                                    <StarIcon width="16" height="16" />
                                </div>
                            </Flex>
                        </Flex>
                    </Card> */}
                </div>
            </main>
        </div>
    );
};

export default FeesDetails; 